import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import ReviewPopup from '../components/ReviewPopup';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const SAVED_FORM_MAP = [
  { key: 'sif_email_form', label: 'Email', path: '/email-generator' },
  { key: 'sif_invoice_form', label: 'Invoice', path: '/invoice-creator' },
  { key: 'sif_wsis_form', label: 'Message', path: '/what-should-i-say' },
];

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { fetchPlanInfo, checkAuth } = useAuth();
  const [status, setStatus] = useState('checking');
  const [packageId, setPackageId] = useState('');
  const [showReview, setShowReview] = useState(false);
  const [savedForm, setSavedForm] = useState(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }
    pollStatus(sessionId, 0);
    // eslint-disable-next-line
  }, [sessionId]);

  const pollStatus = async (sid, attempts) => {
    if (attempts >= 5) {
      setStatus('timeout');
      return;
    }
    try {
      const response = await axios.get(
        `${API_URL}/api/payments/checkout/status/${sid}`,
        { withCredentials: true }
      );
      const data = response.data;
      setPackageId(data.package_id || '');

      if (data.payment_status === 'paid') {
        setStatus('success');
        await fetchPlanInfo();
        await checkAuth();
        // Detect saved form data and show contextual toast
        const found = SAVED_FORM_MAP.find(f => {
          try { return !!localStorage.getItem(f.key); } catch { return false; }
        });
        if (found) {
          setSavedForm(found);
          toast.success(`Your ${found.label} data has been saved — pick up where you left off`, { duration: 6000 });
        }
        // Show review popup after 2.5s delay
        setTimeout(() => setShowReview(true), 2500);
        return;
      } else if (data.status === 'expired') {
        setStatus('expired');
        return;
      }
      setStatus('processing');
      setTimeout(() => pollStatus(sid, attempts + 1), 2000);
    } catch {
      setStatus('error');
    }
  };

  const packageNames = {
    micro_upsell: 'Final Demand Email',
    get_paid_pack: 'Get Paid Pack (7-Day Access)',
    pro_monthly: 'Pro Monthly Plan',
  };

  return (
    <div className="min-h-screen bg-[#050507] flex items-center justify-center px-4" data-testid="payment-success-page">
      <div className="max-w-md w-full text-center">
        {(status === 'checking' || status === 'processing') && (
          <div className="space-y-4">
            <Loader2 className="w-12 h-12 text-[#FF4500] animate-spin mx-auto" />
            <h2 className="text-xl font-semibold text-white">Processing your payment...</h2>
            <p className="text-white/50 text-sm">Please wait while we confirm your payment.</p>
          </div>
        )}

        {status === 'success' && (
          <div className="space-y-4">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto" />
            <h2 className="text-2xl font-bold text-white">Payment Successful!</h2>
            <p className="text-white/60">
              {packageNames[packageId] || 'Your plan'} has been activated.
            </p>
            <div className="flex gap-3 justify-center mt-6">
              <Button
                data-testid="go-to-dashboard-btn"
                onClick={() => navigate('/dashboard')}
                className={savedForm ? 'bg-white/10 hover:bg-white/20 text-white' : 'bg-[#FF4500] hover:bg-[#e03e00] text-white'}
              >
                Go to Dashboard
              </Button>
              <Button
                data-testid="continue-form-btn"
                onClick={() => navigate(savedForm ? savedForm.path : '/invoice-creator')}
                variant={savedForm ? 'default' : 'outline'}
                className={savedForm ? 'bg-[#FF4500] hover:bg-[#e03e00] text-white' : 'border-white/20 text-white hover:bg-white/10'}
              >
                {savedForm ? `Continue ${savedForm.label}` : 'Create Invoice'}
              </Button>
            </div>
          </div>
        )}

        {(status === 'error' || status === 'expired' || status === 'timeout') && (
          <div className="space-y-4">
            <XCircle className="w-16 h-16 text-red-500 mx-auto" />
            <h2 className="text-2xl font-bold text-white">
              {status === 'timeout' ? 'Payment Pending' : 'Payment Issue'}
            </h2>
            <p className="text-white/60">
              {status === 'timeout'
                ? 'We could not confirm your payment yet. Please check your dashboard.'
                : 'Something went wrong. Please contact support if you were charged.'}
            </p>
            <Button
              onClick={() => navigate('/dashboard')}
              className="bg-[#FF4500] hover:bg-[#e03e00] text-white mt-4"
            >
              Go to Dashboard
            </Button>
          </div>
        )}
      </div>

      {/* Review Popup */}
      {showReview && (
        <ReviewPopup
          planType={packageId}
          onClose={() => setShowReview(false)}
        />
      )}
    </div>
  );
}

export function PaymentCancelPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#050507] flex items-center justify-center px-4" data-testid="payment-cancel-page">
      <div className="max-w-md w-full text-center space-y-4">
        <XCircle className="w-16 h-16 text-white/30 mx-auto" />
        <h2 className="text-2xl font-bold text-white">Payment Cancelled</h2>
        <p className="text-white/50">
          No worries! You can upgrade anytime from the app.
        </p>
        <Button
          onClick={() => navigate(-1)}
          className="bg-[#FF4500] hover:bg-[#e03e00] text-white mt-4"
        >
          Go Back
        </Button>
      </div>
    </div>
  );
}
